// Inline `var(--array)` / `var(--object[key])` resolver used during loop text transforms.
// Example: var(--sizes) -> "sm, md, lg", var(--align[start]) -> "flex-start".
import { isLoopIdentifierChar, isLoopIdentifierStart } from './identifiers'
import { skipWhitespace } from './scanner-utils'
import type { LuisArrayVariables } from '../../types'
import type { LuisCompileWarning, LuisObjectVariables } from '../../types'
import type { ParsedLoopVariableReference } from './types'
import { getObjectVariableValue } from '../../variables/object-variable/keys'

// Resolve `var(--array)` references inline while scanning plain source text.
export function replaceArrayVariableReferencesInline(
  source: string,
  arrays: LuisArrayVariables,
  objectVariables: LuisObjectVariables,
  variables: Record<string, string>,
  warnings: LuisCompileWarning[],
): string {
  // Return source unchanged only when no var marker or known variable stores exist.
  if (
    !source.includes('var(')
    || (Object.keys(arrays).length === 0
      && Object.keys(objectVariables).length === 0
      && Object.keys(variables).length === 0)
  ) {
    return source
  } else {
    // Continue scanning inline var references.
  }

  let output = ''
  const joinedCache: Record<string, string> = {}

  // Walk each character and replace when parser finds `var(--name)` tokens.
  for (let index = 0; index < source.length; index += 1) {
    const variableReference = parseArrayVariableReferenceAt(source, index)
    // Keep source character unchanged when token is not a `var(...)` reference.
    if (!variableReference) {
      output += source[index]
      continue
    } else {
      // Continue replacement flow for parsed variable reference.
    }

    if (variableReference.accessKey !== null) {
      const resolvedAccessValue = resolveBracketAccessValue(
        variableReference.name,
        variableReference.accessKey,
        arrays,
        objectVariables,
        variables,
        warnings,
        getSourceLineAt(source, index),
      )
      // Resolve bracket access against array/object variables when possible.
      output += resolvedAccessValue ?? variableReference.raw
      index = variableReference.end - 1
      continue
    } else {
      // Use normalized array value list in inline text.
    }

    const values = arrays[variableReference.name]
    // Keep original token when referenced name is not an array variable.
    if (!values) {
      output += variableReference.raw
      index = variableReference.end - 1
      continue
    } else {
      // Continue joined array replacement for plain var(--array) usage.
    }

    const cachedJoin = joinedCache[variableReference.name]
    // Reuse cached joined array text when loop references repeat.
    if (typeof cachedJoin === 'string') {
      output += cachedJoin
    } else {
      // Join normalized array values once and cache the result.
      const joined = values.join(', ')
      joinedCache[variableReference.name] = joined
      output += joined
    }
    index = variableReference.end - 1
  }

  // Return inline array-resolved source text.
  return output
}

// Parse a `var(--name)` token starting at one source index.
function parseArrayVariableReferenceAt(source: string, startIndex: number): ParsedLoopVariableReference | null {
  // Reject positions that do not start with `var(`.
  if (source.slice(startIndex, startIndex + 4) !== 'var(') {
    // Return null for non-var token starts.
    return null
  } else {
    // Continue parsing var() reference token.
  }

  let cursor = skipWhitespace(source, startIndex + 4)
  // Reject malformed references that do not start with `--`.
  if (source[cursor] !== '-' || source[cursor + 1] !== '-') {
    // Return null when custom property prefix is missing.
    return null
  } else {
    // Continue parsing custom property name.
  }

  const nameStart = cursor
  cursor += 2
  // Reject malformed names that do not have a valid first identifier char.
  if (!isLoopIdentifierStart(source[cursor] ?? '')) {
    // Return null for invalid variable name start.
    return null
  } else {
    // Continue scanning variable identifier.
  }

  cursor += 1
  // Consume custom property identifier chars after `--`.
  while (cursor < source.length && isCustomPropertyNameChar(source[cursor])) {
    cursor += 1
  }

  const name = source.slice(nameStart, cursor)
  let accessKey: string | null = null
  cursor = skipWhitespace(source, cursor)
  // Parse optional `[key]` access after variable name.
  if (source[cursor] === '[') {
    cursor += 1
    cursor = skipWhitespace(source, cursor)
    // Reject empty bracket access when key body is missing.
    if (!isBracketAccessStart(source[cursor] ?? '')) {
      return null
    } else {
      // Continue scanning bracket access body.
    }

    const accessStart = cursor
    cursor += 1
    // Consume key chars until parser reaches closing bracket.
    while (cursor < source.length && isBracketAccessChar(source[cursor])) {
      cursor += 1
    }

    cursor = skipWhitespace(source, cursor)
    // Reject malformed index access without closing bracket.
    if (source[cursor] !== ']') {
      return null
    } else {
      // Continue parsed index finalization.
    }

    accessKey = source.slice(accessStart, cursor)
    cursor += 1
  } else {
    // Keep access key empty when no bracket access is used.
  }

  cursor = skipWhitespace(source, cursor)
  // Reject references that do not close immediately with `)`.
  if (source[cursor] !== ')') {
    // Return null for unterminated var() reference.
    return null
  } else {
    // Continue finalizing parsed var() reference.
  }

  const end = cursor + 1
  // Return parsed array variable reference token.
  return {
    name,
    accessKey,
    end,
    raw: source.slice(startIndex, end),
  }
}

// Resolve `[key]` access against arrays or object variables.
function resolveBracketAccessValue(
  variableName: string,
  accessKey: string,
  arrays: LuisArrayVariables,
  objectVariables: LuisObjectVariables,
  variables: Record<string, string>,
  warnings: LuisCompileWarning[],
  line: number,
): string | null {
  const arrayValue = arrays[variableName]
  const objectValue = objectVariables[variableName]
  // Resolve numeric array access when array variable exists.
  if (arrayValue && /^\d+$/.test(accessKey)) {
    const index = Number.parseInt(accessKey, 10)
    return arrayValue[index] ?? null
  } else if (objectValue) {
    // Resolve object key access using normalized object-key lookup.
    return getObjectVariableValue(objectVariables, variableName, accessKey) ?? null
  } else if (variableName in variables || variableName in arrays) {
    warnings.push({
      code: 'invalid-object-loop-access',
      line,
      message: `Variable "${variableName}" is not an object variable and cannot be accessed with [${accessKey}].`,
    })
    return null
  } else {
    // Keep unresolved var token when referenced variable name does not exist.
    return null
  }
}

// Count the 1-based source line for one token start offset.
function getSourceLineAt(source: string, index: number): number {
  let line = 1

  // Count newlines before the token start to derive source line.
  for (let cursor = 0; cursor < index; cursor += 1) {
    if (source[cursor] === '\n') {
      line += 1
    } else {
      // Keep current line unchanged for non-newline chars.
    }
  }

  // Return 1-based line number for the token.
  return line
}

// Detect valid first chars inside bracket access expressions.
function isBracketAccessStart(char: string): boolean {
  return isLoopIdentifierStart(char) || isDigit(char)
}

// Detect valid chars inside bracket access expressions.
function isBracketAccessChar(char: string): boolean {
  return isLoopIdentifierChar(char) || char === '-' || isDigit(char)
}

// Check whether a char can continue a custom-property name after `--`.
function isCustomPropertyNameChar(char: string): boolean {
  return isLoopIdentifierChar(char) || char === '-'
}

// Detect decimal digits in bracket index expressions.
function isDigit(char: string): boolean {
  return char >= '0' && char <= '9'
}
